import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://financewealth.com.au";

function upsertMeta(selector, attrs) {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => {
            if (k !== "content") el.setAttribute(k, v);
        });
        document.head.appendChild(el);
    }
    el.setAttribute("content", attrs.content);
}

function upsertLink(rel, href, attrs = {}) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function upsertJsonLd(id, data) {
    let el = document.head.querySelector(`script[type="application/ld+json"][data-id="${id}"]`);
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.setAttribute("data-id", id);
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

/**
 * <Seo /> — sets title, description, canonical, OG/Twitter tags per route.
 * Also injects optional page-specific JSON-LD structured data.
 */
export const Seo = ({
    title,
    description,
    keywords,
    canonicalPath,
    ogImage,
    jsonLd, // optional: object or array of objects
    robots, // optional: e.g. "noindex,follow"
}) => {
    const location = useLocation();
    const path = canonicalPath || location.pathname || "/";
    const canonicalUrl = `${SITE_URL}${path}`;

    useEffect(() => {
        if (title) document.title = title;
        if (description) {
            upsertMeta('meta[name="description"]', { name: "description", content: description });
            upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
            upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
        }
        if (keywords) {
            upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
        }
        upsertMeta('meta[property="og:title"]', { property: "og:title", content: title || document.title });
        upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
        upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
        upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Finance Wealth" });
        upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
        upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title || document.title });
        if (ogImage) {
            upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
            upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
        }
        upsertLink("canonical", canonicalUrl);

        // Robots directive (defaults to index,follow if unset by removing any prior override)
        if (robots) {
            upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
        } else {
            const r = document.head.querySelector('meta[name="robots"]');
            if (r) r.remove();
        }

        // Always clean up any page-scope JSON-LD from the previous route
        document.head
            .querySelectorAll('script[type="application/ld+json"][data-scope="page"]')
            .forEach((n) => n.remove());

        if (jsonLd) {
            const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
            items.forEach((data, i) => {
                const el = document.createElement("script");
                el.type = "application/ld+json";
                el.setAttribute("data-scope", "page");
                el.setAttribute("data-id", `page-${i}`);
                el.textContent = JSON.stringify(data);
                document.head.appendChild(el);
            });
        }
    }, [title, description, keywords, canonicalUrl, ogImage, jsonLd, robots]);

    return null;
};

export default Seo;
